class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onBackgroundClick = this.handleBackgroundClick.bind(this);
    this.onDiscountCart = (event) => this.refreshDiscountForm(event.detail?.cart);
    this.discountRequestSerial = 0;
    this.discountApplySerial = 0;

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.attachOverlayListeners();
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    document.addEventListener("lp:discount-cart", this.onDiscountCart);
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      this.discountCartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.refreshDiscountForm(event.cartData);
      });
    }
    this.refreshDiscountForm();
    this.restoreAfterDiscountRedirect();
  }

  disconnectedCallback() {
    document.removeEventListener("lp:discount-cart", this.onDiscountCart);
    this.discountCartUnsubscriber?.();
    ++this.discountRequestSerial;
  }

  refreshDiscountForm(cartData) {
    const isCart = (data) => data && (
      Array.isArray(data.discount_codes) ||
      Array.isArray(data.cart_level_discount_applications) ||
      Array.isArray(data.items)
    );
    if (isCart(cartData)) {
      ++this.discountRequestSerial;
      this.setDiscountFormVisibility(cartData);
      return;
    }
    if (this.discountApplying) return;
    const serial = ++this.discountRequestSerial;
    const root = window.Shopify?.routes?.root || "/";
    return fetch(root + "cart.js", {
      credentials: "same-origin",
      cache: "no-store",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Unable to check cart discount");
        return response.json();
      })
      .then((data) => {
        if (this.isConnected && serial === this.discountRequestSerial && isCart(data)) {
          this.setDiscountFormVisibility(data);
        }
      })
      .catch(() => {
        // Keep the server-rendered state so an unavailable check cannot block entry.
      });
  }

  setDiscountFormVisibility(cartData) {
    const form = this.querySelector("[data-cart-drawer-discount-form]");
    if (!form) return;

    const confirmed = this.discountCodesFrom(cartData).filter((entry) => entry.applicable === true);
    const applied = !this.discountRenderFailed && confirmed.length > 0;

    const moveFocus = applied && form.contains(document.activeElement);
    if (form.hidden !== applied) form.hidden = applied;
    if (applied) {
      this.discountMessage("Discount code " + confirmed.map((entry) => entry.code).join(", ") + " applied.");
      const message = this.querySelector("[data-cart-drawer-discount-message]");
      if (message) message.dataset.state = "applied";
    } else if (!this.discountApplying && this.querySelector("[data-cart-drawer-discount-message]")?.dataset.state === "applied") {
      this.discountMessage("");
    }
    if (moveFocus) {
      (this.querySelector("#CartDrawer-Checkout") || this.querySelector(".drawer__close"))?.focus({ preventScroll: true });
    }
  }

  discountCodesFrom(cartData) {
    const codes = new Map();
    (cartData.discount_codes || []).forEach((entry) => {
      const code = (typeof entry === "string" ? entry : entry?.code)?.trim();
      if (code) codes.set(code.toUpperCase(), { code, applicable: entry.applicable });
    });
    const applications = [...(cartData.cart_level_discount_applications || [])];
    (cartData.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        applications.push(allocation.discount_application);
      });
    });
    applications.forEach((entry) => {
      if (entry?.type === "discount_code" && entry.title) {
        codes.set(entry.title.toUpperCase(), { code: entry.title, applicable: true });
      }
    });
    return [...codes.values()];
  }

  discountMessage(text, error = false) {
    const message = this.querySelector("[data-cart-drawer-discount-message]");
    if (!message) return;
    message.textContent = text;
    message.hidden = !text;
    message.dataset.error = String(error);
    message.dataset.state = error ? "error" : "status";
    const input = this.querySelector("[data-cart-drawer-discount-form] input");
    if (input) error ? input.setAttribute("aria-invalid", "true") : input.removeAttribute("aria-invalid");
  }

  async applyDiscount(code) {
    if (this.discountApplying || !code) return;
    this.discountApplying = true;
    ++this.discountApplySerial;
    ++this.discountRequestSerial;
    const root = window.Shopify?.routes?.root || "/";
    const form = this.querySelector("[data-cart-drawer-discount-form]");
    const input = form?.elements.discount;
    const button = form?.querySelector('[type="submit"]');
    const buttonText = button?.textContent || "Apply";
    const restoreFocus = form?.contains(document.activeElement);
    let updatedCart = null;
    if (form) form.setAttribute("aria-busy", "true");
    if (input) input.disabled = true;
    if (button) { button.disabled = true; button.textContent = "Applying…"; }
    this.discountMessage("Applying code…");

    try {
      const options = { credentials: "same-origin", cache: "no-store", signal: AbortSignal.timeout(12000) };
      const currentResponse = await fetch(root + "cart.js", options);
      if (!currentResponse.ok) throw new Error("Could not check your cart. Please try again.");
      const current = await currentResponse.json();
      if (!this.isConnected) return;
      const codes = this.discountCodesFrom(current).map((entry) => entry.code)
        .filter((value) => value.toUpperCase() !== code.toUpperCase());
      codes.push(code);
      const response = await fetch(root + "cart/update.js", {
        method: "POST", credentials: "same-origin", signal: AbortSignal.timeout(12000),
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          discount: codes.join(","),
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname + window.location.search,
        }),
      });
      const data = await response.json();
      if (!response.ok || Number(data.status) >= 400) {
        throw new Error(data.description || "Could not apply this code. Please try again.");
      }
      if (!Array.isArray(data.items) || typeof data.total_price !== "number") {
        throw new Error("Could not confirm your cart. Please try again.");
      }
      updatedCart = data;
      ++this.discountRequestSerial;
      if (!this.isConnected) return;
      await this.renderDiscountCart(data);
      this.discountRenderFailed = false;
      this.setDiscountFormVisibility(data);
      const applied = this.discountCodesFrom(data).some((entry) =>
        entry.code.toUpperCase() === code.toUpperCase() && entry.applicable === true
      );
      if (applied) {
        document.dispatchEvent(new CustomEvent("lp:discount-cart", { detail: { cart: data, code } }));
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          try {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-drawer-discount", cartData: data, drawerUpdated: true });
          } catch (_) {
            // An unrelated subscriber must not undo a confirmed drawer update.
          }
        }
      } else {
        this.discountMessage("This code does not apply to your current cart. Check the code and its conditions.", true);
      }
    } catch (error) {
      if (!this.isConnected) return;
      if (updatedCart) {
        // The discount write may have succeeded even if section rendering failed.
        // Keep a retry entry instead of leaving stale prices with no recovery.
        this.discountRenderFailed = true;
        this.setDiscountFormVisibility(updatedCart);
        this.discountMessage("The code was submitted, but cart prices could not refresh. Please try Apply again.", true);
      } else {
        this.discountMessage(error.name === "TimeoutError" || error.name === "AbortError"
          ? "The request timed out. Please try again." : error.message, true);
      }
    } finally {
      this.discountApplying = false;
      const currentForm = this.querySelector("[data-cart-drawer-discount-form]");
      const currentInput = currentForm?.elements.discount;
      const currentButton = currentForm?.querySelector('[type="submit"]');
      currentForm?.removeAttribute("aria-busy");
      if (currentInput) { currentInput.disabled = false; currentInput.value = code; }
      if (currentButton) { currentButton.disabled = false; currentButton.textContent = buttonText; }
      if (restoreFocus && this.isConnected && this.classList.contains("active")) {
        (currentForm?.hidden ? this.querySelector("#CartDrawer-Checkout") : currentInput)?.focus({ preventScroll: true });
      }
    }
  }

  async renderDiscountCart(cartData) {
    let sections = cartData.sections || {};
    let drawerHtml = this.getSectionDOM(sections["cart-drawer"] || "", "#CartDrawer");
    if (!drawerHtml) {
      const root = window.Shopify?.routes?.root || "/";
      const response = await fetch(root + "cart?sections=" + this.getSectionsToRender().map((section) => section.id).join(","), {
        credentials: "same-origin", cache: "no-store", signal: AbortSignal.timeout(12000),
      });
      if (!response.ok) throw new Error("Could not refresh cart prices");
      sections = await response.json();
      drawerHtml = this.getSectionDOM(sections["cart-drawer"] || "", "#CartDrawer");
    }
    if (!drawerHtml) throw new Error("Could not refresh cart prices");
    if (!this.isConnected) return;

    const currentDrawer = this.querySelector("#CartDrawer");
    if (!currentDrawer) throw new Error("Cart drawer unavailable");
    const note = this.querySelector("#CartDrawer-Note")?.value;
    const noteOpen = this.querySelector("#Details-CartDrawer")?.open;
    const scrolls = [".drawer__inner", ".drawer__cart-items-wrapper", "cart-drawer-items"]
      .map((selector) => ({ selector, top: this.querySelector(selector)?.scrollTop || 0 }));
    currentDrawer.innerHTML = drawerHtml.innerHTML;
    if (note !== undefined && this.querySelector("#CartDrawer-Note")) this.querySelector("#CartDrawer-Note").value = note;
    if (noteOpen !== undefined && this.querySelector("#Details-CartDrawer")) this.querySelector("#Details-CartDrawer").open = noteOpen;
    if (this.classList.contains("active")) {
      trapFocus(currentDrawer, this.querySelector(".drawer__inner") || this.querySelector(".drawer__close"));
    }
    scrolls.forEach(({ selector, top }) => { const node = this.querySelector(selector); if (node) node.scrollTop = top; });
    this.getSectionsToRender().filter((section) => section.id !== "cart-drawer").forEach((section) => {
      const node = document.getElementById(section.id);
      const html = sections[section.id] && this.getSectionInnerHTML(sections[section.id]);
      if (node && html != null) node.innerHTML = html;
    });
    this.attachOverlayListeners();
  }

  restoreAfterDiscountRedirect() {
    const storageKey = "luminipet:reopen-cart-drawer-after-discount";

    try {
      const reopenUntil = Number(sessionStorage.getItem(storageKey));
      if (!reopenUntil) return;

      sessionStorage.removeItem(storageKey);
      if (Date.now() > reopenUntil) return;

      requestAnimationFrame(() => this.open());
    } catch (error) {
      // The discount redirect remains functional when browser storage is unavailable.
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    const stickyCartLink = document.querySelector("#cart-icon-bubble-sticky");

    if (stickyCartLink) {
      stickyCartLink.setAttribute("role", "button");
      stickyCartLink.setAttribute("aria-haspopup", "dialog");
      stickyCartLink.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(stickyCartLink);
      });
      stickyCartLink.addEventListener("keydown", (event) => {
        if (event.code.toUpperCase() === "SPACE") {
          event.preventDefault();
          this.open(stickyCartLink);
        }
      });
    }
  }

  open(triggeredBy) {
    // The portrait loading panel hides discount controls; refresh when real cart contents open.
    if (!this.classList.contains("lp-apo-cart-pending") || !document.body.classList.contains("lp-portrait-pdp")) {
      this.refreshDiscountForm();
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    if (
      this.activeElement &&
      !this.activeElement.classList.contains("quick-add__submit")
    ) {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState?.sections) return;

    const drawerInner = this.querySelector(".drawer__inner");
    if (drawerInner?.classList.contains("is-empty")) {
      drawerInner.classList.remove("is-empty");
    }

    if (typeof parsedState.item_count === "number") {
      this.classList.toggle("is-empty", parsedState.item_count === 0);
    } else {
      this.classList.remove("is-empty");
    }

    this.productId = parsedState.id;

    let didUpdate = false;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionInnerHTML = this.getSectionInnerHTML(
        sectionHtml,
        section.selector,
      );

      if (sectionInnerHTML == null) return;

      sectionElement.innerHTML = sectionInnerHTML;
      didUpdate = true;
    });

    if (!didUpdate) return;

    setTimeout(() => {
      this.attachOverlayListeners();
      this.open();
    });
  }

  handleBackgroundClick(event) {
    const inner = this.querySelector(".drawer__inner");
    if (!inner) return;
    // If click is inside cart-drawer but outside drawer__inner, close
    if (!inner.contains(event.target)) {
      this.close();
    }
  }

  attachOverlayListeners() {
    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) {
      overlay.addEventListener("click", this.close.bind(this));
    }
    const continueShopping = this.querySelector(".drawer-continue-shopping");
    if (continueShopping) {
      continueShopping.addEventListener("click", this.close.bind(this));
    }

    // Ensure background click-to-close works even if overlay is replaced
    this.removeEventListener("click", this.onBackgroundClick);
    this.addEventListener("click", this.onBackgroundClick);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (!html) return null;

    const parsedHtml = new DOMParser().parseFromString(html, "text/html");
    const target = selector
      ? parsedHtml.querySelector(selector)
      : parsedHtml.body;

    return target ? target.innerHTML : null;
  }
  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
      {
        id: "cart-icon-bubble-sticky",
      },
    ];
  }
  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }
  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

document.addEventListener("submit", (event) => {
  const form = event.target.closest("[data-cart-drawer-discount-form]");
  const drawer = form?.closest("cart-drawer");
  if (!drawer) return;

  event.preventDefault();
  // Only this drawer's coupon submission is owned here. Stop legacy bubbling
  // submit handlers from navigating after the AJAX request has started.
  event.stopImmediatePropagation();

  const input = form.elements.discount;
  const code = input.value.trim();
  if (!code) {
    input.focus();
    return;
  }

  drawer.applyDiscount(code);
}, true);

class CartDrawerItems extends CartItems {
  // For cart drawer we want to re-render from the cart-drawer section,
  // not from main-cart-items like the base CartItems.onCartUpdate does.
  connectedCallback() {
    // Override subscription to cartUpdate to use drawer-specific refresh
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-drawer-items" || event.source === "cart-drawer-discount" && event.drawerUpdated) {
          return;
        }
        this.onDrawerCartUpdate();
      },
    );
  }

  onDrawerCartUpdate() {
    const drawer = this.closest("cart-drawer");
    if (drawer?.discountApplying) return;
    const serial = drawer?.discountApplySerial;
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        if (drawer?.discountApplying || drawer && serial !== drawer.discountApplySerial) return;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const drawerSection = html.querySelector("#CartDrawer");
        const currentDrawer = document.querySelector("#CartDrawer");
        if (drawerSection && currentDrawer) {
          currentDrawer.innerHTML = drawerSection.innerHTML;
          currentDrawer.closest("cart-drawer")?.refreshDiscountForm();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble-sticky",
        section: "cart-icon-bubble-sticky",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
